<template>
  <div id="live-feed-post">
    <Breadcrumbs
      :crumbs="[
        { name: globals.info.school_name, link: `${pathPrefix}/` },
        { name: crumbName, link: `${pathPrefix}/live-feed` },
        { name: post.id.toString(), link: `${pathPrefix}/live_feeds/${post.id}` },
      ]"
    />
    <LoadingIndicator v-if="!post?.status" />
    <div
      v-else
      class="page-container"
    >
      <div class="page-container-item">
        <PGalleria
          :value="post.live_feed_images"
          :responsive-options="galleriaResponsiveOptions"
          :circular="true"
          :show-thumbnails="post.live_feed_images.length > 1"
          container-style="width: 100%; max-width: 640px; margin: 0 auto; border: none;"
          class="live-feed-post-images"
        >
          <template #item="slotProps">
            <div class="galleria-main-image-wrapper">
              <img
                :src="slotProps.item.url"
                :alt="slotProps.item.alt_text"
                class="post-image galleria-main-image"
                width="600"
                height="450"
                loading="lazy"
              />
            </div>
          </template>
          <template
            v-if="post.live_feed_images.length > 1"
            #thumbnail="slotProps"
          >
            <img
              :src="slotProps.item.url"
              :alt="slotProps.item.alt_text"
              class="galleria-thumbnail"
              loading="lazy"
            />
          </template>
        </PGalleria>
        <div class="live-feed-post-info">
          <p class="date">
            {{ readableDate }}
          </p>
          <div class="author-row">
            <img
              :src="post.avatar || defaultAvatarUrl"
              :alt="$t('common.avatar', { name: post.author_name })"
              class="author-avatar"
              @error="handleAvatarError"
            />
            <p class="author">
              {{ post.author_name }}
            </p>
          </div>
          <div class="status">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="post.status" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useMainStore } from '../../../stores/mainStore';
import Breadcrumbs from '../../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../../components/global/LoadingIndicator.vue';
import dayjs from 'dayjs';
import { clientWorkWindowEvent } from '../../../helpers/event-dispatch-helpers';
import { getFileTypeFromUrl } from '../../../helpers/metadata';
import { useNuxtApp, useRoute, useAsyncData, createError, useHead } from '#imports';

defineOptions({
  name: 'LiveFeedPost',
});

const galleriaResponsiveOptions = [
  { breakpoint: '1400px', numVisible: 5 },
  { breakpoint: '1024px', numVisible: 4 },
  { breakpoint: '768px', numVisible: 3 },
  { breakpoint: '560px', numVisible: 2 },
];

const { $axios, $t, $d } = useNuxtApp();
const mainStore = useMainStore();
const globals = computed(() => mainStore.globals);
const domain = computed(() => mainStore.domain);
const pathPrefix = computed(() => mainStore.pathPrefix);
const customSections = computed(() => mainStore.customSections);

onMounted(() => {
  clientWorkWindowEvent('live-feed-post');
});
const postId = useRoute().params.id;
const feedPost = mainStore.getLiveFeedPost($axios.defaults.baseURL);

if (!feedPost) {
  throw createError({ statusCode: 404, statusMessage: 'Live feed configuration not found' });
}

const { url, params } = feedPost;

const { data: postData, error } = await useAsyncData(async () => {
  const { data } = await $axios.get(`${url}/${postId}`, { params, cache: { ignoreCache: false } });
  return data.live_feed;
});
const post = computed(() => postData.value || {});

if (!post.value && error.value.statusCode) {
  throw createError({
    statusCode: error.value.statusCode || 500,
    statusMessage: error.value.message || 'Unexpected server error',
  });
}
const fetchedPostData = post.value;
const metaLiveFeedPostUrl = `https://${domain.value}${pathPrefix.value}/live_feeds/${postId}`;

let metaLiveFeedPostImage = '';

if (fetchedPostData.live_feed_images.length > 0) {
  metaLiveFeedPostImage = fetchedPostData.live_feed_images[0].url;
}
const metaCoverImageType = getFileTypeFromUrl(metaLiveFeedPostImage);

useHead({
  title: fetchedPostData.title,
  meta: [
    {
      hid: 'og:type',
      vmid: 'og:type',
      property: 'og:type',
      content: 'website',
    },
    {
      hid: 'og:url',
      vmid: 'og:url',
      property: 'og:url',
      content: metaLiveFeedPostUrl,
    },
    {
      hid: 'og:title',
      vmid: 'og:title',
      property: 'og:title',
      content: fetchedPostData.title,
    },
    {
      hid: 'og:description',
      vmid: 'og:description',
      property: 'og:description',
      content: fetchedPostData.status,
      tagPriority: 'critical',
    },
    {
      hid: 'og:image',
      vmid: 'og:image',
      property: 'og:image',
      content: metaLiveFeedPostImage,
      tagPriority: 'critical',
    },
    {
      hid: 'og:image:type',
      vmid: 'og:image:type',
      property: 'og:image:type',
      content: metaCoverImageType,
      tagPriority: 'critical',
    },
    { hid: 'twitter:site', name: 'twitter:site', content: metaLiveFeedPostUrl },
    { hid: 'twitter:title', name: 'twitter:title', content: fetchedPostData.title },
    { hid: 'twitter:description', name: 'twitter:description', content: fetchedPostData.status },
    { hid: 'twitter:image', name: 'twitter:image', content: metaLiveFeedPostImage, tagPriority: 'critical' },
  ],
});

watch(error, (e) => {
  if (e) {
    throw createError({ statusCode: e?.response?.status });
  }
});

const crumbName = computed(() => {
  const customSection = customSections.value.find((s) => s.type === 'live_feeds');
  return customSection ? customSection.name : $t('pages.liveFeed.sectionName');
});

const defaultAvatarUrl = 'https://www.apptegy.net/fallback/circled_default_avatar.png';

function handleAvatarError(evt) {
  evt.target.src = defaultAvatarUrl;
}

const READABLE_DATE_OPTIONS = { year: 'numeric', month: 'long', day: 'numeric' };

function getReadableDate(post) {
  const input = post.formatted_date?.replace(' ', 'T') ?? post.published_at;
  if (!input) return '';
  const d = dayjs(input);
  return d.isValid() ? $d(d.toDate(), READABLE_DATE_OPTIONS) : '';
}

const readableDate = computed(() => getReadableDate(post.value));
</script>

<style scoped lang="scss">
#live-feed-post {
  background: #fff;
  min-height: 100vh;
  .page-container {
    max-width: 1024px;
  }
  .page-container-item {
    border: none;
    display: flex;
    flex-direction: column;
    .live-feed-post-info {
      margin: 20px 0;
      .status {
        font-size: 24px;
        font-weight: 400;
        color: #000000;
        overflow-x: auto;
      }
      .date {
        margin-top: 0;
        margin-bottom: 8px;
        color: #595959;
      }

      .author-row {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 0;
        margin-bottom: 8px;
      }

      .author-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        object-fit: cover;
        flex-shrink: 0;
      }

      .author {
        margin: 0;
        color: #595959;
      }
    }
    .live-feed-post-images {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin: 0 auto 20px;
      gap: 0;
      width: 100%;
      max-width: 640px;
    }
    /* Hide thumbnail nav arrows when disabled (all images fit in the strip). */
    :deep([class*='p-galleria-thumbnail-prev']:disabled),
    :deep([class*='p-galleria-thumbnail-next']:disabled),
    :deep([class*='p-galleria-thumbnail-prev'][aria-disabled='true']),
    :deep([class*='p-galleria-thumbnail-next'][aria-disabled='true']) {
      display: none;
    }
    :deep(.p-galleria) {
      width: 100%;
    }
    :deep(.p-galleria-thumbnails-wrapper),
    :deep(.p-galleria-thumbnails-container) {
      max-width: 100%;
    }
    .galleria-main-image-wrapper {
      width: 100%;
      max-width: 640px;
      background: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      margin-bottom: 10px;
    }
    .post-image.galleria-main-image {
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      object-position: center;
      display: block;
    }
    :deep(.p-galleria-thumbnails-content) {
      max-width: 100%;
      box-sizing: border-box;
      background: #ffffff;
      border: 1px #f3f3f3 solid;
      border-radius: 4px;
    }
    :deep(.p-galleria-thumbnail):focus-visible {
      border: 1px solid var(--p-galleria-thumbnail-nav-button-focus-ring-color);
      border-radius: 4px;
      outline: 1px auto var(--p-galleria-thumbnail-nav-button-focus-ring-color);
    }
    .galleria-thumbnail {
      width: 80px;
      height: 80px;
      min-width: 80px;
      object-fit: cover;
      display: block;
    }
    @media (max-width: 560px) {
      .galleria-thumbnail {
        width: 60px;
        height: 60px;
        min-width: 60px;
      }
    }
    .post-image {
      max-width: 100%;
      height: auto;
      margin: 4px 0;
    }
  }
}
</style>
